import React, { useState, useRef, useEffect } from 'react';
import { 
  MessageCircle, 
  Send, 
  Phone, 
  Video, 
  MoreVertical,
  Smile,
  Paperclip,
  Search,
  Users,
  Settings,
  Circle
} from 'lucide-react';

const Chat = () => {
  const [selectedChat, setSelectedChat] = useState(1);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  // Données d'exemple pour les conversations
  const [conversations] = useState([
    {
      id: 1,
      name: 'Équipe Commerciale',
      type: 'group',
      avatar: '👥',
      lastMessage: 'Nouveau prospect dans le pipeline',
      lastTime: '14:30',
      unread: 3,
      online: true,
      members: ['Commercial A', 'Commercial B', 'Manager Vente']
    },
    {
      id: 2,
      name: 'Équipe Technique',
      type: 'group',
      avatar: '🔧',
      lastMessage: 'Installation prévue demain à 9h',
      lastTime: '13:45',
      unread: 1,
      online: true,
      members: ['Technicien 1', 'Technicien 2', 'Chef d\'équipe']
    },
    {
      id: 3,
      name: 'Marie Dubois',
      type: 'direct',
      avatar: '👩',
      lastMessage: 'Merci pour les informations',
      lastTime: '12:20',
      unread: 0,
      online: false,
      role: 'Commercial'
    },
    {
      id: 4,
      name: 'Direction',
      type: 'group',
      avatar: '👔',
      lastMessage: 'Réunion mensuelle reportée',
      lastTime: '11:15',
      unread: 0,
      online: true,
      members: ['Directeur', 'Manager Vente', 'Manager RH']
    }
  ]);

  // Messages pour la conversation sélectionnée
  const [messages, setMessages] = useState({
    1: [
      {
        id: 1,
        sender: 'Commercial A',
        content: 'Bonjour équipe ! J\'ai un nouveau prospect très intéressé par une installation 9kWc',
        time: '14:20',
        isOwn: false
      },
      {
        id: 2,
        sender: 'Manager Vente',
        content: 'Excellent ! As-tu déjà fait une première estimation ?',
        time: '14:22',
        isOwn: false
      },
      {
        id: 3,
        sender: 'Vous',
        content: 'Je peux m\'occuper du devis si besoin. Quel est le budget du client ?',
        time: '14:25',
        isOwn: true
      },
      {
        id: 4,
        sender: 'Commercial A',
        content: 'Budget autour de 18-20k€. Je vous envoie les détails par email',
        time: '14:30',
        isOwn: false
      }
    ],
    2: [
      {
        id: 1,
        sender: 'Chef d\'équipe',
        content: 'Installation Marseille prévue demain 9h. Équipe : Pierre et Thomas',
        time: '13:40',
        isOwn: false
      },
      {
        id: 2,
        sender: 'Technicien 1',
        content: 'OK, matériel préparé. Durée estimée 6h',
        time: '13:45',
        isOwn: false
      }
    ]
  });

  const selectedConversation = conversations.find(c => c.id === selectedChat);
  const currentMessages = messages[selectedChat] || [];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [currentMessages]);

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      const message = {
        id: currentMessages.length + 1,
        sender: 'Vous',
        content: newMessage,
        time: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
        isOwn: true
      };
      
      setMessages(prev => ({
        ...prev,
        [selectedChat]: [...(prev[selectedChat] || []), message]
      }));
      
      setNewMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
            <MessageCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Chat d'équipe</h1>
            <p className="text-gray-600">Communication interne temps réel</p>
          </div>
        </div>
      </div>

      {/* Interface de chat */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden" style={{ height: '600px' }}>
        <div className="flex h-full">
          {/* Liste des conversations */}
          <div className="w-80 border-r border-gray-200 flex flex-col">
            {/* Header conversations */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">Conversations</h3>
                <button className="p-1 hover:bg-gray-100 rounded-lg">
                  <Settings className="w-4 h-4 text-gray-600" />
                </button>
              </div>
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Liste des conversations */}
            <div className="flex-1 overflow-y-auto">
              {conversations.map(conv => (
                <button
                  key={conv.id}
                  onClick={() => setSelectedChat(conv.id)}
                  className={`w-full p-4 text-left hover:bg-gray-50 transition-colors border-l-2 ${
                    selectedChat === conv.id ? 'bg-blue-50 border-l-blue-500' : 'border-l-transparent'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="relative">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-lg">
                        {conv.avatar}
                      </div>
                      {conv.online && (
                        <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium text-gray-900 truncate">{conv.name}</h4>
                        <span className="text-xs text-gray-500">{conv.lastTime}</span>
                      </div>
                      <p className="text-sm text-gray-600 truncate">{conv.lastMessage}</p>
                      <div className="flex items-center justify-between mt-1">
                        <div className="flex items-center gap-1">
                          {conv.type === 'group' && (
                            <Users className="w-3 h-3 text-gray-400" />
                          )}
                          <span className="text-xs text-gray-400">
                            {conv.type === 'group' ? `${conv.members.length} membres` : conv.role}
                          </span>
                        </div>
                        {conv.unread > 0 && (
                          <div className="w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-medium">
                            {conv.unread}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Zone de chat */}
          <div className="flex-1 flex flex-col">
            {selectedConversation && (
              <>
                {/* Header chat */}
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                          {selectedConversation.avatar}
                        </div>
                        {selectedConversation.online && (
                          <Circle className="absolute -bottom-1 -right-1 w-3 h-3 fill-green-500 text-green-500" />
                        )}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{selectedConversation.name}</h4>
                        <p className="text-sm text-gray-600">
                          {selectedConversation.type === 'group' 
                            ? `${selectedConversation.members.length} membres` 
                            : selectedConversation.online ? 'En ligne' : 'Hors ligne'
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="p-2 hover:bg-gray-100 rounded-lg">
                        <Phone className="w-4 h-4 text-gray-600" />
                      </button>
                      <button className="p-2 hover:bg-gray-100 rounded-lg">
                        <Video className="w-4 h-4 text-gray-600" />
                      </button>
                      <button className="p-2 hover:bg-gray-100 rounded-lg">
                        <MoreVertical className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {currentMessages.map(message => (
                    <div
                      key={message.id}
                      className={`flex ${message.isOwn ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${
                        message.isOwn
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}>
                        {!message.isOwn && (
                          <p className="text-xs font-medium mb-1 opacity-75">{message.sender}</p>
                        )}
                        <p className="text-sm">{message.content}</p>
                        <p className={`text-xs mt-1 ${
                          message.isOwn ? 'text-blue-100' : 'text-gray-500'
                        }`}>
                          {message.time}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input de message */}
                <div className="p-4 border-t border-gray-200">
                  <div className="flex items-end gap-3">
                    <button className="p-2 hover:bg-gray-100 rounded-lg">
                      <Paperclip className="w-5 h-5 text-gray-600" />
                    </button>
                    <div className="flex-1 relative">
                      <textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Tapez votre message..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none max-h-24"
                        rows="1"
                      />
                    </div>
                    <button className="p-2 hover:bg-gray-100 rounded-lg">
                      <Smile className="w-5 h-5 text-gray-600" />
                    </button>
                    <button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim()}
                      className="p-3 bg-blue-500 text-white rounded-2xl hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;